import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDataStore } from '../store/useDataStore'
import { useStorageConfigStore } from '../store/useStorageConfigStore'
import { getOssFileUrl, generateObjectKey } from '../services/ossService'
import { downloadMediaFilesFromServer } from '../services/mediaDownloader'
import { useLazyImage } from './LazyImage'

interface MarketplaceProject {
  id: number | null
  share_id: string
  project_uuid: string
  title: string
  description: string | null
  author_name: string | null
  cover_image_url: string | null
  created_at: string
  views_count: number
  likes_count: number
  forks_count: number
  tags: string | null
  is_published?: number
}

// 随机生成旋转角度 (-2deg 到 2deg)
const getRandomRotation = () => Math.random() * 4 - 2

// 定义一组清新淡雅的边框颜色
const BORDER_COLORS = [
  'border-blue-200',
  'border-green-200',
  'border-purple-200',
  'border-pink-200',
  'border-yellow-200',
  'border-indigo-200',
  'border-teal-200',
  'border-orange-200',
];

export const MarketplaceView = () => {
  const navigate = useNavigate()
  const [projects, setProjects] = useState<MarketplaceProject[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [sort, setSort] = useState('newest')
  const [isForking, setIsForking] = useState(false)
  const { config: storageConfig, initConfig } = useStorageConfigStore()
  const { userUuid } = useDataStore()

  // 为每个项目生成固定的随机属性（旋转角度、边框颜色）
  const [cardStyles, setCardStyles] = useState<{rotation: number, borderColor: string}[]>([])

  // 初始化存储配置
  useEffect(() => {
    if (!storageConfig) {
      initConfig().catch(err => {
        console.error('初始化存储配置失败:', err)
      })
    }
  }, [storageConfig, initConfig])

  useEffect(() => {
    fetchProjects()
  }, [sort, searchTerm])

  const fetchProjects = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams({
        sort,
        search: searchTerm,
        page: '1',
        limit: '50' // 加载更多以填充瀑布流
      })
      const response = await fetch(`/api/marketplace?${params}`)
      const data = await response.json()
      if (data.success) {
        setProjects(data.data.projects)
        // 生成对应数量的随机样式
        setCardStyles(data.data.projects.map(() => ({
          rotation: getRandomRotation(),
          borderColor: BORDER_COLORS[Math.floor(Math.random() * BORDER_COLORS.length)]
        })))
      }
    } catch (error) {
      console.error('Failed to fetch marketplace projects:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleFork = async (e: React.MouseEvent, project: MarketplaceProject) => {
    e.stopPropagation() // 防止触发卡片点击
    if (!confirm(`确认要将 "${project.title}" 添加到你的项目列表吗？`)) return

    setIsForking(true)
    try {
      const response = await fetch(`/api/share/project/${project.share_id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
      })
      const data = await response.json()
      
      if (data.success && data.data) {
        const projectData = data.data
        const shareInfo = data.shareInfo // 获取分享信息，包含原用户的 userUuid
        const originalUserUuid = shareInfo?.userUuid // 原项目所属用户
        const originalProjectUuid = projectData.uuid // 原项目 UUID
        
        const newProject = {
          ...projectData,
          name: `${project.title} (Fork)`,
          uuid: crypto.randomUUID(),
          items: (projectData.items || []).map((item: any) => ({
             ...item,
             uuid: crypto.randomUUID()
          }))
        }
        
        const win = window as any
        if (win.importProjectFromData) {
            // 先下载媒体文件，再导入项目
            // 这样可以避免渲染时因为文件还没下载完成而出现 404 错误
            if (userUuid && originalUserUuid && originalProjectUuid) {
              console.log('[Marketplace] 开始下载媒体文件（在导入项目之前）...')
              console.log(`[Marketplace] 原项目: userUuid=${originalUserUuid}, projectUuid=${originalProjectUuid}`)
              console.log(`[Marketplace] 当前用户: ${userUuid}`)
              try {
                // 创建一个临时项目对象，使用原项目的 UUID 来下载文件
                const projectForDownload = {
                  ...newProject,
                  uuid: originalProjectUuid // 使用原项目 UUID 来构建下载路径
                }
                // 从原用户路径下载，保存到当前用户的 IndexedDB
                const mediaResult = await downloadMediaFilesFromServer(originalUserUuid, [projectForDownload], userUuid)
                console.log(`[Marketplace] 媒体文件下载完成: 成功 ${mediaResult.success} 个, 失败 ${mediaResult.failed} 个, 跳过 ${mediaResult.skipped} 个`)
              } catch (mediaError) {
                console.error('[Marketplace] 下载媒体文件失败:', mediaError)
                // 媒体下载失败不影响项目导入的成功状态
              }
            } else {
              console.warn('[Marketplace] 缺少必要信息，跳过媒体文件下载', { userUuid, originalUserUuid, originalProjectUuid })
            }
            
            // 媒体文件下载完成后，再导入项目
            await win.importProjectFromData(newProject)
            
            alert('项目已导入！')
            navigate('/')
        } else {
            console.error('importProjectFromData bridge not found')
            alert('导入失败: 桥接函数未找到')
        }
        
        if (project.id !== null) {
          await fetch(`/api/marketplace/${project.id}/action`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ type: 'fork' })
          })
        }
      }
    } catch (error) {
      console.error('Fork failed:', error)
      alert('导入失败，请重试')
    } finally {
      setIsForking(false)
    }
  }

  // 获取缩略图 URL（使用对象存储时生成 COS/OSS URL）
  const getThumbnailUrl = (coverImageUrl: string | null, _project: MarketplaceProject) => {
    if (!coverImageUrl) return null
    // 如果是 Base64 格式（data:image/...），直接返回
    if (coverImageUrl.startsWith('data:image/')) {
      return coverImageUrl
    }
    // 如果已经是完整的URL（包括OSS URL），直接返回
    if (coverImageUrl.startsWith('http://') || coverImageUrl.startsWith('https://')) {
      return coverImageUrl
    }
    // 如果是文件API路径，检查是否使用对象存储
    if (coverImageUrl.startsWith('/api/files/')) {
      // 如果使用对象存储（腾讯云 COS 或阿里云 OSS），生成对象存储 URL
      if (storageConfig?.useOSS && storageConfig.bucket && storageConfig.region) {
        // 解析路径：/api/files/{userUuid}/{projectUuid}/{fileId}/thumbnail
        // 或者：/api/files/{userUuid}/{projectUuid}/{fileId}
        const parts = coverImageUrl.split('/').filter(p => p) // 过滤空字符串
        // parts: ['api', 'files', userUuid, projectUuid, fileId, 'thumbnail'] 或 ['api', 'files', userUuid, projectUuid, fileId]
        if (parts.length >= 5) {
          const fileUserUuid = parts[2]
          const projectUuid = parts[3]
          let fileId = parts[4]
          
          // 确保 fileId 有扩展名
          if (!fileId.includes('.')) {
            fileId = `${fileId}.jpg`
          }
          
          // 生成对象键（对象存储中的文件路径）
          const objectKey = generateObjectKey(fileUserUuid, projectUuid, fileId)
          // 生成对象存储 URL（腾讯云 COS 或阿里云 OSS）
          const provider = storageConfig.provider || 'aliyun'
          return getOssFileUrl(objectKey, storageConfig.bucket, storageConfig.region, storageConfig.endpoint || undefined, provider)
        }
      }
      // 如果不使用对象存储或解析失败，使用后端 API 路径
      // 如果已经是缩略图URL，直接返回
      if (coverImageUrl.includes('/thumbnail')) return coverImageUrl
      // 否则添加 /thumbnail
      return `${coverImageUrl}/thumbnail`
    }
    return coverImageUrl
  }

  // 骨架屏组件
  const SkeletonCard = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-2 space-y-2">
      <div className="w-full h-36 bg-gray-100 rounded-lg animate-pulse"></div>
      <div className="h-3 bg-gray-100 rounded w-3/4 animate-pulse"></div>
      <div className="h-3 bg-gray-100 rounded w-1/2 animate-pulse"></div>
    </div>
  )

  // 懒加载图片组件
  const LazyProjectImage: React.FC<{ src: string; alt: string; title: string }> = ({ src, alt, title }) => {
    const [isLoaded, setIsLoaded] = useState(false)
    const [hasError, setHasError] = useState(false)
    const [isVisible, setIsVisible] = useState(false)
    const imgRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible(true)
            observer.disconnect()
          }
        },
        { rootMargin: '100px', threshold: 0.1 }
      )

      if (imgRef.current) {
        observer.observe(imgRef.current)
      }

      return () => observer.disconnect()
    }, [])

    if (hasError) {
      return (
        <div className="w-full py-12 bg-gradient-to-br from-indigo-50 to-pink-50 flex flex-col items-center justify-center text-gray-400 rounded-lg">
          <div className="text-4xl mb-2 opacity-50">
            {title ? title.substring(0, 1).toUpperCase() : '🎨'}
          </div>
          <span className="text-xs font-medium opacity-60">封面加载失败</span>
        </div>
      )
    }

    return (
      <div ref={imgRef} className="relative min-h-[120px]">
        {!isLoaded && (
          <div className="absolute inset-0 bg-gray-100 animate-pulse rounded-lg" />
        )}
        {isVisible && (
          <img
            src={src}
            alt={alt}
            className={`w-full h-auto object-cover block rounded-lg image-constrained transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => setIsLoaded(true)}
            onError={() => setHasError(true)}
          />
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col font-sans text-gray-800 relative h-full overflow-y-auto custom-scrollbar">
      <main className="flex-1 py-8 w-full relative z-10 px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10">
            <div>
              <h1 className="text-3xl font-black text-gray-800 mb-2 tracking-tight">灵感广场</h1>
              <p className="text-gray-400 font-medium">✨ 探索全世界创作者分享的精彩画布</p>
            </div>
            
            <div className="flex gap-4 items-center w-full md:w-auto">
              <div className="relative group flex-1 md:w-64">
                <input 
                  type="text" 
                  placeholder="探索灵感..." 
                  className="w-full pl-10 pr-4 py-3 bg-white border border-pink-100 rounded-2xl text-sm focus:ring-4 focus:ring-pink-100 focus:border-[#ff5c5c] transition-all shadow-sm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 group-focus-within:text-[#ff5c5c] transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              
              <select 
                className="bg-white px-4 py-3 rounded-2xl border border-pink-100 text-sm font-bold text-gray-500 cursor-pointer outline-none hover:border-[#ff5c5c] transition-all shadow-sm"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
              >
                <option value="newest">✨ 最新发布</option>
                <option value="popular">🔥 热门排行</option>
                <option value="likes">❤️ 最受喜爱</option>
                <option value="forks">🍴 收藏最多</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className="gallery-grid">
              {[...Array(10)].map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : projects.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-32 text-center">
              <div className="text-8xl mb-6 animate-bounce">🎨</div>
              <h2 className="text-3xl font-bold text-gray-800 mb-3">空空如也</h2>
              <p className="text-gray-500 max-w-md mx-auto">
                还没有人分享画布呢。<br/>
                创建一个分享链接，它就会出现在这里！
              </p>
            </div>
          ) : (
            <div className="gallery-grid pb-20">
              {projects.map((project, index) => {
                const thumbnailUrl = getThumbnailUrl(project.cover_image_url, project)
                const coverUrl = thumbnailUrl || project.cover_image_url
                const { rotation, borderColor } = cardStyles[index] || { rotation: 0, borderColor: 'border-gray-200' }
                
                return (
                  <div 
                    key={project.share_id || index} 
                    className="relative group cursor-pointer"
                    style={{ 
                      transform: `rotate(${rotation}deg)`,
                      transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.03) rotate(0deg) translateY(-3px)'
                      e.currentTarget.style.zIndex = '10'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = `rotate(${rotation}deg)`
                      e.currentTarget.style.zIndex = '1'
                    }}
                    onClick={() => window.open(`/share/${project.share_id}`, '_blank')}
                  >
                    <div className={`bg-white p-2 pb-2 rounded-xl shadow-md group-hover:shadow-2xl transition-all duration-300 border-2 ${borderColor}`}>
                      <div className="relative bg-gray-50 overflow-hidden rounded-lg mb-2.5 flex items-center justify-center image-container min-h-[120px]">
                         {coverUrl ? (
                           <LazyProjectImage 
                             src={coverUrl} 
                             alt={project.title}
                             title={project.title}
                           />
                         ) : (
                           <div className="w-full py-12 bg-gradient-to-br from-indigo-50 to-pink-50 flex flex-col items-center justify-center text-gray-400 rounded-lg">
                             <div className="text-4xl mb-2 opacity-50">
                               {project.title ? project.title.substring(0, 1).toUpperCase() : '🎨'}
                             </div>
                             <span className="text-xs font-medium opacity-60">无预览图</span>
                           </div>
                         )}

                         <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300 rounded-lg pointer-events-none"></div>
                      </div>

                      <div className="px-1">
                        <h3 className="font-comic font-bold text-gray-800 text-[13px] leading-tight mb-0.5 line-clamp-2 group-hover:text-blue-600 transition-colors">
                          {project.title}
                        </h3>
                        
                        {project.description && (
                          <p className="text-[9px] text-gray-500 mb-1 line-clamp-2 leading-relaxed font-handwriting">
                            {project.description}
                          </p>
                        )}

                        <div className="flex items-center justify-between mt-1 pt-1 border-t border-dashed border-gray-100">
                          <div className="flex items-center gap-1.5 text-xs text-gray-500">
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] text-white font-bold shadow-sm ${borderColor.replace('border-', 'bg-').replace('200', '400')}`}>
                              {project.author_name ? project.author_name[0].toUpperCase() : 'A'}
                            </div>
                            <span className="max-w-[70px] truncate text-[10px]">{project.author_name || '匿名艺术家'}</span>
                          </div>

                          <div className="flex items-center gap-2 opacity-60 group-hover:opacity-100 transition-opacity">
                            <span className="flex items-center text-xs text-gray-400" title="访问次数">
                               👁️ {project.views_count || 0}
                            </span>
                            
                            <button 
                              onClick={(e) => handleFork(e, project)}
                              className={`bg-gray-50 hover:bg-white text-gray-400 hover:text-blue-500 p-1.5 rounded-lg transition-all border border-transparent hover:border-blue-100 shadow-sm ${isForking ? 'opacity-50 cursor-not-allowed' : ''}`}
                              title="获取此画布"
                              disabled={isForking}
                            >
                              {isForking ? (
                                <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="12" r="10" strokeOpacity="0.25"/>
                                  <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
                                </svg>
                              ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M6 3v12"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M18 9a9 9 0 0 1-9 9"/>
                                </svg>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {index % 3 === 0 && (
                      <div className={`absolute -top-0 left-1/2 -translate-x-1/2 w-20 h-6 ${borderColor.replace('border-', 'bg-').replace('200', '100')}/80 backdrop-blur-sm -rotate-1 shadow-sm pointer-events-none z-20 opacity-90 mix-blend-multiply transform rounded-sm`}></div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </main>

      <style>{`
        .font-comic {
          font-family: "Comic Sans MS", "Chalkboard SE", "Comic Neue", sans-serif;
        }
        /* 图片容器宽高比限制: 最低16:9, 最高16:18 */
        .image-container {
          position: relative;
          width: 100%;
          container-type: inline-size;
        }
        .image-container img.image-constrained {
          width: 100%;
          height: auto;
          object-fit: cover;
          /* 最低高度: 16:9 比例 = 56.25% of width */
          min-height: 56.25cqi;
          /* 最高高度: 16:24 比例 = 150% of width */
          max-height: 150cqi;
        }
        /* 兼容性回退 - 不支持container queries的浏览器 */
        @supports not (container-type: inline-size) {
          .image-container img.image-constrained {
            min-height: 120px;
            max-height: 250px;
          }
        }
        /* Grid 布局 - 精确控制列数，水平居中 */
        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 220px));
          gap: 16px;
          justify-content: center;
          padding: 0 16px;
        }
        @media (min-width: 640px) {
          .gallery-grid {
            grid-template-columns: repeat(3, minmax(0, 220px));
          }
        }
        @media (min-width: 768px) {
          .gallery-grid {
            grid-template-columns: repeat(4, minmax(0, 220px));
          }
        }
        @media (min-width: 1024px) {
          .gallery-grid {
            grid-template-columns: repeat(5, minmax(0, 220px));
          }
        }
        @media (min-width: 1280px) {
          .gallery-grid {
            grid-template-columns: repeat(5, minmax(0, 240px));
            gap: 20px;
          }
        }
      `}</style>
    </div>
  )
}
